/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { Helmet } from 'react-helmet';
import { withStyles } from '@material-ui/core/styles';
import {
    object, shape, arrayOf, string, bool,
} from 'prop-types';
import Grid from '@material-ui/core/Grid';
import ReactMarkdown from 'react-markdown';
import { useUIDSeed } from 'react-uid';
import ArticleBlock from './Partials/ArticleBlock';
import ListBlock from './Partials/ListBlock';
import DesktopMobile from '../../../helpers/DesktopMobile/DesktopMobile';
import { trackData } from '../../../helpers/tracking/common/commonTrackingHelpers';
import LinkOrATag from '../GraphqlCommonComponents/LinkOrATag/LinkOrATag';

const styles = (theme) => ({
    container: {
        [theme?.breakpoints?.down('sm')]: {
            maxWidth: theme?.palette?.tiers?.container?.maxWidth?.downSmall,
            margin: '0 auto',
        },
        [theme?.breakpoints?.down('md')]: {
            maxWidth: theme?.palette?.tiers?.container?.maxWidth?.downMedium,
            margin: '0 auto',
        },
        [theme?.breakpoints?.up('md')]: {
            maxWidth: 1440,
            margin: '0 auto',
        },
        [theme?.breakpoints?.up('lg')]: {
            maxWidth: 1440,
            margin: '0 auto',
        },
    },
    innerContainer: {
        maxWidth: 1150,
        margin: 'auto',
        Width: '80%',
    },
    banner: {
        '& img': {
            width: '100%',
            display: 'block',
            [theme.breakpoints.down(600)]: {
                marginBottom: 10,
            },
        },
    },
    bannerText: {
        textAlign: 'center',
        fontSize: 30,
        lineHeight: 1.2,
        [theme.breakpoints.down(600)]: {
            fontSize: 20,
            padding: '0 10px',
        },
    },
    topBannerImage: {
        position: 'relative',
    },
    content: {
        position: 'absolute',
        width: '100%',
    },
    mainCopyContainer: {
        fontFamily: 'Roboto Slab',
        '& h2,h3,h4,h5,h6,p,span': {
            fontSize: 56,
            margin: 0,
            [theme.breakpoints.down(1280)]: {
                fontSize: 35,
            },
            [theme.breakpoints.down(1024)]: {
                fontSize: 60,
                padding: 25,
            },
            [theme.breakpoints.down(768)]: {
                padding: '0px 30px',
                fontSize: 45,
            },
            [theme.breakpoints.down(600)]: {
                fontSize: 35,
            },
            [theme.breakpoints.down(440)]: {
                fontSize: 21,
            },
        },
    },
    subCopyContainer: {
        width: '50%',
        fontSize: 18,
        [theme.breakpoints.down(1280)]: {
            width: '60%',
        },
        [theme.breakpoints.down(1024)]: {
            width: '90%',
            fontSize: 21,
        },
        [theme.breakpoints.down(600)]: {
            fontSize: 14,
        },
        [theme.breakpoints.down(440)]: {
            fontSize: 12,
        },
        '& h2,h3,h4,h5,h6,p,span': {
            marginTop: 0,
        },
    },
});
const topBannerImage = (imagePath, topBanner, classes) => {
    let topBannerStyle = {};
    let subCopyStyle = {};
    if (topBanner?.alignment?.toLowerCase() === 'center') {
        topBannerStyle =  { textAlign: 'center', top: '15px' };
        subCopyStyle = { margin: '0 auto',  color: topBanner?.sub_copy_color?.color };
    } else if (topBanner?.alignment?.toLowerCase() === 'left') {
        topBannerStyle =  {
            textAlign: 'left', top: '6%', width: '60%',  paddingLeft: '4%',
        };
        subCopyStyle = { color: topBanner?.sub_copy_color?.color, textAlign: 'center' };
    } else if (topBanner?.alignment?.toLowerCase() === 'right') {
        topBannerStyle =  { textAlign: 'right' };
        subCopyStyle = { color: topBanner?.sub_copy_color?.color };
    }
    return (
        <div className={classes.topBannerImage}>
            {imagePath && <img src={imagePath} alt="Top Banner" />}
            <div className={classes.content} style={topBannerStyle}>
                <div className={classes.mainCopyContainer} style={{ color: topBanner?.main_copy_color?.color }}>
                    {topBanner?.main_copy && <ReactMarkdown source={topBanner?.main_copy} escapeHtml={false} />}
                </div>
                <div className={classes.subCopyContainer} style={subCopyStyle}>
                    {topBanner?.sub_copy && <ReactMarkdown source={topBanner?.sub_copy} escapeHtml={false} />}
                </div>
            </div>
        </div>
    );
};

const ConnectBlock = ({
    classes, data, seo, brand,
}) => {
    const seed = useUIDSeed();
    trackData({
        untData: data,
        action: 'track_connection_collections_data',
    });
    // Top Banner
    const { banner: topBanner } = data[0] || {};
    const { url: desktopImagePath = '' } = topBanner?.desktop_image || {};
    const { url: mobileImagePath  = '' } = topBanner?.mobile_image || {};
    return (
        <>
            {
                seo.seo_searchable
                    ? (
                        <Helmet>
                            <title>{seo.page_title}</title>
                            {seo.keywords && <meta name="keywords" content={seo.keywords} />}
                            {seo.meta_description && <meta name="description" content={seo.meta_description} />}
                        </Helmet>
                    ) : null
            }
            <div className={classes.container}>
                <Grid className={classes.banner} item xs={12}>
                    <DesktopMobile
                        mobile={() =>  topBannerImage(mobileImagePath, topBanner, classes)}
                        desktop={() =>  topBannerImage(desktopImagePath, topBanner, classes)}
                    />
                </Grid>
                <div className={classes.innerContainer}>
                    <Grid container>
                        {data?.slice(1)?.map((list) => {
                            const { banner, articles, two_column } = list;
                            const blockKey = Object.keys(list)[0];
                            switch (blockKey) {
                                case 'banner':
                                    // Top Banner Text
                                    if (banner?.desktop_image === null) {
                                        return (
                                            <Grid key={seed(list)} item xs={12} className={classes.bannerText}>
                                                <ReactMarkdown source={banner?.main_copy} />
                                            </Grid>
                                        );
                                    }
                                    // Bottom Banner
                                    if (banner?.link?.href) {
                                        const newHref = banner.link.href.includes('/blog') || banner.link.href.includes('/articles') ? `https://www.${brand?.domain}.com${banner.link.href}`
                                            : banner.link.href;
                                        const { url: desktopUrl = '', filename = '' } = banner?.desktop_image || {};
                                        const { url: mobileUrl = '', filename: mobileFilename = '' } = banner?.mobile_image || {};
                                        return (
                                            <Grid key={seed(list)} className={classes.banner} item xs={12}>
                                                <LinkOrATag href={newHref}>
                                                    <DesktopMobile
                                                        mobile={() => <img src={mobileUrl} alt={mobileFilename} />}
                                                        desktop={() => <img src={desktopUrl} alt={filename} />}
                                                    />
                                                </LinkOrATag>
                                            </Grid>
                                        );
                                    }
                                    return null;
                                case 'two_column':
                                    return (<ListBlock key={seed(list)} data={two_column} brand={brand} />);
                                case 'articles':
                                    // Articles
                                    return (<ArticleBlock key={seed(list)} data={articles} brand={brand} />);
                                default:
                                    return null;
                            }
                        })}
                    </Grid>
                </div>
            </div>
        </>
    );
};

ConnectBlock.propTypes = {
    classes: object.isRequired,
    data: arrayOf(shape({
        banner: object,
        two_column: object,
        articles: object,
    })).isRequired,
    seo: shape({
        breadcrumb: arrayOf(
            shape({
                title: string.isRequired,
                href: string.isRequired,
            }).isRequired,
        ),
        keywords: string,
        meta_description: string,
        seo_searchable: bool.isRequired,
        page_title: string,
    }).isRequired,
    brand: shape({
        domain: string.isRequired,
    }).isRequired,
};
export default withStyles(styles)(ConnectBlock);
